// Box and polygon geometry, ported from frontend/tools/bbox_tool.py and view_scale.py.
// tests/unit/canvas-golden.test.ts checks these against fixtures generated from the Python code.
export interface Rect { x1: number; y1: number; x2: number; y2: number }
export type Pt = [number, number];
export type Handle = "tl" | "tr" | "bl" | "br" | "tm" | "bm" | "lm" | "rm";

/** Handles are sized in screen pixels (bbox_tool.HANDLE_SIZE) and converted to scene units at use. */
export const HANDLE_SIZE = 9;
/** A dragged box smaller than this in either direction is discarded (BBoxTool.mouse_release). */
export const MIN_BOX = 4;
/** Order matters: the first handle containing the point wins (BBoxItem.HANDLES). */
export const HANDLES: Handle[] = ["tl", "tr", "bl", "br", "tm", "bm", "lm", "rm"];

/** view_scale.scene_length: a screen length in scene units; a non-positive or non-finite scale leaves it unchanged. */
export function sceneLength(screenPx: number, scale: number): number {
  const s = Number(scale);
  if (!(s > 0) || s === Infinity) return screenPx;
  return screenPx / s;
}

export const normalized = (r: Rect): Rect => ({
  x1: Math.min(r.x1, r.x2), y1: Math.min(r.y1, r.y2), x2: Math.max(r.x1, r.x2), y2: Math.max(r.y1, r.y2),
});
export const rectW = (r: Rect) => r.x2 - r.x1;
export const rectH = (r: Rect) => r.y2 - r.y1;
export const rectsEqual = (a: Rect, b: Rect) => a.x1 === b.x1 && a.y1 === b.y1 && a.x2 === b.x2 && a.y2 === b.y2;

/** QRectF.contains: the edges count as inside. */
export const rectContains = (r: Rect, p: Pt) => p[0] >= r.x1 && p[0] <= r.x2 && p[1] >= r.y1 && p[1] <= r.y2;

export function rectsIntersect(a: Rect, b: Rect): boolean {
  const n = normalized(a), m = normalized(b);
  return n.x1 <= m.x2 && m.x1 <= n.x2 && n.y1 <= m.y2 && m.y1 <= n.y2;
}

/** BBoxItem._handle_rects, as [left, top, right, bottom] squares centred on the corners and edge midpoints. */
export function handleRects(r: Rect, scale: number): Record<Handle, Rect> {
  const s = sceneLength(HANDLE_SIZE, scale), hs = s / 2;
  const cx = (r.x1 + r.x2) / 2, cy = (r.y1 + r.y2) / 2;
  const sq = (x: number, y: number): Rect => ({ x1: x - hs, y1: y - hs, x2: x - hs + s, y2: y - hs + s });
  return {
    tl: sq(r.x1, r.y1), tr: sq(r.x2, r.y1), bl: sq(r.x1, r.y2), br: sq(r.x2, r.y2),
    tm: sq(cx, r.y1), bm: sq(cx, r.y2), lm: sq(r.x1, cy), rm: sq(r.x2, cy),
  };
}

/** BBoxItem._handle_at: only a selected box has handles. */
export function handleAt(r: Rect, p: Pt, scale: number, selected: boolean): Handle | null {
  if (!selected) return null;
  const rects = handleRects(r, scale);
  for (const h of HANDLES) if (rectContains(rects[h], p)) return h;
  return null;
}

/** BBoxItem.shape().contains: the rectangle, plus the handles when selected. */
export function boxShapeContains(r: Rect, p: Pt, scale: number, selected: boolean): boolean {
  if (rectContains(r, p)) return true;
  return selected && HANDLES.some((h) => rectContains(handleRects(r, scale)[h], p));
}

/** BBoxItem._resize_to: move the dragged edges to the pointer, then normalise (a drag past the far side flips the box). */
export function resizeRect(r: Rect, handle: Handle, p: Pt): Rect {
  const n = { ...r };
  if (handle.includes("l")) n.x1 = p[0];
  if (handle.includes("r")) n.x2 = p[0];
  if (handle.includes("t")) n.y1 = p[1];
  if (handle.includes("b")) n.y2 = p[1];
  return normalized(n);
}

export const CURSOR_FOR_HANDLE: Record<Handle, string> = {
  tl: "nwse-resize", br: "nwse-resize", tr: "nesw-resize", bl: "nesw-resize",
  tm: "ns-resize", bm: "ns-resize", lm: "ew-resize", rm: "ew-resize",
};

// ── Polygons: hit testing (editing lives in shapes.ts) ─────────────────────────────────────────────────────────────────────────
export function pointInPolygon(pts: Pt[], p: Pt): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function polygonBounds(pts: Pt[]): Rect {
  const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}
